import { useMemo } from 'react'
import { Dialog, IconButton } from '@mui/material'
import { Dismiss24Regular } from '@/components/Icon'
import DocumentsBrowser from './DocumentsBrowser'
import DocumentsPickerFooter from './DocumentsPickerFooter'
import styles from './documentsPickerDialog.module.scss'
import { useDocumentsLibrary } from './useDocumentsLibrary'
import { useDocumentsPickerSelection } from './useDocumentsPickerSelection'

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSelectDocuments: (items: import('./documentsDemoData').DocumentItem[]) => void,
 *   maxSelectable?: number,
 * }} props
 */
export default function DocumentsPickerDialog({
  open,
  onClose,
  onSelectDocuments,
  maxSelectable = 10,
}) {
  const { selectedIds, onToggleSelect, selectedCount } = useDocumentsPickerSelection(open, maxSelectable)
  const { items: libraryItems } = useDocumentsLibrary()

  const handleConfirm = () => {
    const items = libraryItems.filter((item) => selectedIds.has(item.id))
    if (items.length === 0) return
    onSelectDocuments(items)
    onClose()
  }

  const pickerProps = useMemo(
    () => ({ pickerMode: true, selectedIds, onToggleSelect }),
    [selectedIds, onToggleSelect],
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      aria-labelledby="documents-picker-title"
      slotProps={{ paper: { className: styles.dialogPaper, elevation: 0 } }}
    >
      <IconButton
        type="button"
        aria-label="Close documents"
        onClick={onClose}
        size="small"
        className={styles.closeButton}
      >
        <Dismiss24Regular className={styles.closeIcon} aria-hidden />
      </IconButton>
      <div className={styles.body}>
        <DocumentsBrowser pickerMode inDialog {...pickerProps} />
      </div>
      <DocumentsPickerFooter
        selectedCount={selectedCount}
        maxSelectable={maxSelectable}
        onCancel={onClose}
        onConfirm={handleConfirm}
      />
    </Dialog>
  )
}
